import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    cancelMaintenanceService,
    deleteMaintenanceService,
    getMaintenanceServiceAlerts,
    getMaintenanceServiceList,
} from '../../services/MaintenanceServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';
import MaintenanceServiceAlerts from './MaintenanceServiceAlerts';

function PreventiveMaintenanceTable() {
    const [data, setData] = useState({ records: [] });
    const [alerts, setAlerts] = useState([]);
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(() => getDateRangeFromSalesSettings(salesSettings), [salesSettings]);

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status === true || p.status === 1 || p.status === 'Active')
                .map((p) => ({ value: p.id, label: p.plant_alias })));
            setIsInitialized(true);
        });
    }, [cookies]);

    const settingsSummary = useMemo(() => getSalesSettingsSummary(salesSettings, plants, []), [salesSettings, plants]);

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;
        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }
        setIsLoading(true);
        const params = {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        };
        return Promise.all([
            getMaintenanceServiceList(cookies, params),
            getMaintenanceServiceAlerts(cookies, { plant_id: plantId }),
        ])
            .then(([listResponse, alertResponse]) => {
                setAlerts(alertResponse.maintenance_service_alerts || []);
                setData({
                    records: (listResponse.maintenance_service_list || []).map((row) => {
                        const isActive = row.status === true || row.status === 1 || row.status === 'Active';
                        return {
                            ...row,
                            record_type_label: row.record_type === 'BREAKDOWN' ? 'Breakdown' : 'Preventive',
                            status_label: isActive ? 'Active' : 'Cancelled',
                            hide_row_edit: isActive ? 'No' : 'Yes',
                            hide_row_delete: 'No',
                        };
                    }),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load service records.' }, 'Load Failed');
                setData({ records: [] });
                setAlerts([]);
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings]);

    useEffect(() => {
        if (isInitialized) fetchRecords();
    }, [fetchRecords, isInitialized]);

    const columns = useMemo(() => ([
        { fieldName: 'service_date', headerName: 'Date' },
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'equipment_name', headerName: 'Equipment' },
        { fieldName: 'record_type_label', headerName: 'Type' },
        { fieldName: 'service_type_label', headerName: 'Service' },
        { fieldName: 'next_service_due_date', headerName: 'Next Due' },
        { fieldName: 'next_service_due_hm', headerName: 'Due HM', align: 'right' },
        { fieldName: 'service_cost', headerName: 'Cost', align: 'right' },
        { fieldName: 'vendor_name', headerName: 'Vendor' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ]), []);

    const handleEdit = (id) => navigate(`/PreventiveMaintenanceEdit/${id}`);

    const handleDelete = (id) => {
        const row = data.records.find((r) => String(r.id) === String(id));
        const isActive = row?.status_label === 'Active';
        Swal.fire({
            title: isActive ? 'Cancel this service record?' : 'Delete this service record?',
            icon: 'warning',
            showCancelButton: true,
        }).then((result) => {
            if (!result.isConfirmed) return;
            const action = isActive
                ? cancelMaintenanceService(cookies, id)
                : deleteMaintenanceService(cookies, id);
            action
                .then(() => fetchRecords())
                .catch((error) => displayError(error?.response?.data || error, isActive ? 'Cancel Failed' : 'Delete Failed'));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="MAINTENANCE"
                    title="Service & Breakdown Records"
                    subtitle={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onAdd={() => navigate('/PreventiveMaintenance')}
                    onRefresh={fetchRecords}
                    onExportCSV={() => exportAsExcel(columns, data.records, 'maintenance_service')}
                    isLoading={isLoading}
                >
                    <MaintenanceServiceAlerts alerts={alerts} />
                    <ReactTableWrapper
                        data={data.records}
                        columns={columns}
                        isLoading={isLoading}
                        onRowEdit={handleEdit}
                        onRowDelete={handleDelete}
                    />
                </ListPageLayout>
                <SalesListSettingsModal
                    show={showSettings}
                    onClose={() => setShowSettings(false)}
                    settings={salesSettings}
                    plants={plants}
                    onSave={(nextSettings) => {
                        saveSalesListSettings(cookies.myUserId, nextSettings);
                        setSalesSettings(nextSettings);
                        setShowSettings(false);
                    }}
                />
            </div>
        </div>
    );
}

export default PreventiveMaintenanceTable;
